/*
 * Utility for implementing multiple pages in an SPA.
 */
import { getConfig, exposeFunctions } from "./core.js";

/** @type {{ [key: number]: HTMLDivElement }} */
const SPAs = {};

export { }; // expose the typedef to other modules

/**
 * Additional information provided when opening a page.
 * @typedef {Object} OpenPageInfo
 * @property {number} [spaId] - The id of the SPA container. Defaults to "spa-container".
 * @property {number} [level] - Pages of greater or equal levels are popped out because they have lower priority. Level 0 has no priority.
 */

/**
 * @param {number} [spaId]
 * @returns {HTMLDivElement}
 */
function getContainer(spaId) {
	const id = spaId || 1;
	let container = SPAs[id];
	if (!container) {
		const elem = document.getElementById("spa-" + id);
		container = /** @type {HTMLDivElement} */ (elem);
	}
	if (!container) {
		if (spaId) {
			console.error(`No element with id '${id}' found.`);
			return null;
		}
		const elem = document.querySelector('.spa-container');
		container = /** @type {HTMLDivElement} */ (elem);
		if (!container) {
			console.error("No element with class spa-container found.");
			return null;
		}
	}
	SPAs[id] = container;
	return container;
};

/**
 * @param {number} [spaId]
 * @returns {boolean}
 */
export function closePage(spaId = undefined) {
	const container = getContainer(spaId);
	if (container?.childElementCount >= 2) {

		const lastPage = container.lastElementChild;
		const previous = lastPage.previousElementSibling;

		lastPage.dispatchEvent(new Event("page-closed"));
		previous.dispatchEvent(new Event("page-resumed"));

		previous.classList.add("active");
		lastPage.remove();
		return true;
	}
	return false;
}

function onWindowPopstate() {
	if (!closePage())
		window.history.back();
}

function getPageId(name, options) {
	let append = options?.level;
	if (!append) {
		append = 1000 + (Date.now() % 1000);
	}
	return name + '-' + append;
}

/**
 * Open a page in an SPA.
 * @param {string} name
 * @param {OpenPageInfo} [options]
 * @returns {HTMLDivElement}
 */
export function openPage(name, options = undefined) {
	if (!name) {
		console.error("Page name not provided");
		return null;
	}

	const container = getContainer(options?.spaId);
	if (!container)
		return null;

	const isPWA = getConfig().isProgressiveWebApp;

	let page = null;

	let last = /** @type {HTMLDivElement} */(container.lastElementChild);
	if (last)
		last.classList.remove("active");

	const level = Number(options?.level);
	if (level) {
		while (last) {
			const l = Number(last.dataset.level);

			// check if last is the same page as an earlier opened page
			// if it is, we can reuse it and stop removing pages
			if (l == level && last.getAttribute("name") == name) {
				page = last;
				break;
			}

			// check if last is a page with a greater or equal level, as
			// it would be of lower priority to the one we are opening
			else if (!l || l >= level) {

				const previous = /** @type {HTMLDivElement} */ (last.previousElementSibling);
				last.remove(); // remove this page
				last = previous;

				if (container.childElementCount > 1 && isPWA) {
					// TODO: also remove from window.history
				}
			}

			// cannot remove a page with a smaller level
			// value because that has a higher priority
			else break;
		}
	}

	if (!page) {
		page = document.createElement('div');
		page.id = getPageId(name, options);
		page.className = "spa-page";
		page.setAttribute('name', name);
		container.appendChild(page);
	}
	else {
		const n = Number(page.dataset.reuse ?? 0);
		page.dataset.reuse = (n + 1).toString();
	}

	if (level)
		page.dataset.level = level.toString();

	if (container.childElementCount > 1 && isPWA) {
		window.history.pushState({ page: page.id }, "", "#" + name);

		// a named function is used so it is registered only once
		window.addEventListener("popstate", onWindowPopstate);
	}

	page.classList.add("active");
	return page;
}

exposeFunctions({ openPage, closePage });

export default openPage;

